import {
  PageNavigationTabs,
  type PageNavigationTabItem,
} from '@/components/patterns/page-navigation-tabs'
import { usePathHelper, type PathResult } from '@/hooks/usePathHelper'
import { useRouteOrganizationIdParam } from '@/hooks/useRouteOrganizationId'
import { useAbility, type AppAbility } from '@/lib/casl'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import { Boxes, Flower2, Gift, LibraryBig, Tags } from 'lucide-react'

type ProductCatalogTab = Pick<PageNavigationTabItem, 'label' | 'icon'> & {
  id: string
  canShow: (ability: AppAbility) => boolean
  getPath: (path: ReturnType<typeof usePathHelper>) => PathResult
}

const productCatalogTabs: ProductCatalogTab[] = [
  {
    id: 'products',
    label: 'Catalog',
    icon: Flower2,
    canShow: (ability) => ability.can('list', 'products'),
    getPath: (path) => path.products.index(),
  },
  {
    id: 'collections',
    label: 'Collections',
    icon: LibraryBig,
    canShow: (ability) => ability.can('list', 'collections'),
    getPath: (path) => path.collections.index(),
  },
  {
    id: 'add-ons',
    label: 'Add-ons',
    icon: Gift,
    canShow: (ability) => ability.can('list', 'products'),
    getPath: (path) => path.addOnSets.index(),
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: Boxes,
    canShow: (ability) => ability.can('list', 'products'),
    getPath: (path) => path.inventory.index(),
  },
  {
    id: 'price-changes',
    label: 'Price Changes',
    icon: Tags,
    canShow: (ability) => ability.can('edit', 'products'),
    getPath: (path) => path.priceChanges.index(),
  },
]

function linkForTabPath(pathResult: PathResult, orgId: OrganizationIdInput): PathResult['link'] {
  return orgId ? pathResult.using({ orgId }).link : pathResult.link
}

export default function ProductCatalogNav({ active }: { active?: string }) {
  const ability = useAbility()
  const path = usePathHelper()
  const orgId = useRouteOrganizationIdParam()
  const visibleTabs = productCatalogTabs.filter((tab) => tab.canShow(ability))

  return (
    <PageNavigationTabs
      ariaLabel="Product catalog sections"
      items={visibleTabs.map((tab) => ({
        key: tab.id,
        label: tab.label,
        icon: tab.icon,
        isActive: active === tab.id,
        link: {
          ...linkForTabPath(tab.getPath(path), orgId),
          activeOptions: { exact: true },
        },
      }))}
    />
  )
}
